import React, { useState, useEffect, useMemo } from 'react'
import useStore from 'state/knovStore'
import cn from 'classnames'
import styles from 'components/panels/user-profile.module.scss'
import FilterOptions from 'components/filters/FilterOptions'
import SearchBar from 'components/panels/SearchBar'
import StreamOrder from 'components/panels/StreamOrder'
import ArtistSongsOrder from 'components/panels/ArtistSongsOrder'
import ProfileEarnings from 'components/panels/ProfileEarnings'
import StreamOptions from 'components/panels/StreamOptions'
import { UserStream } from 'components/filters/FilterIcon'
import SearchIcon from 'components/shared/icons/SearchIcon'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useSlideToPanel from 'refactor/hooks/useSlideToPanel'
import queryClient from 'api/queryClient'
import { getQuestsQueryKey } from 'refactor/hooks/api/useGetQuests'
import useGetUser from 'refactor/hooks/api/useGetUser'
import useUserOrdinalsBadge from 'hooks/useUserOrdinalsBadge'
import { Participants, nonUserParticipants, ParticipantsFromIds } from 'components/people/People'
import { cacheUser, getCachedQuest, resolveUser } from 'state/cache'
import { getDepth, withDepth, Filter } from 'components/filters/useStreamFilters'
import { PanelState } from 'state/PanelState'
import { Quest } from 'types/quests'
import useOpenRight from 'refactor/hooks/useOpenRight'
import { clearNotificationCount } from 'utils/notificationUtils'
import WithTooltip from 'components/shared/WithTooltip'
import ShareTray from 'components/shared/ShareTray'
import { isMobile } from 'lib/device'
import api from 'api/api'
import { getShowcaseShareCardUrl, getShowcaseShareUrl } from 'utils/shareUtils'
import {
    UserPlus,
    UserCheck,
    FileText,
    Circle,
    Share2,
    Search,
    Pencil,
    MessageCircle,
    Music,
} from 'lucide-react'
import useUserBannerUpload from 'components/users/useUserBannerUpload'
import MessageUserButton from 'components/users/MessageUserButton'
import { getParticipantIdsFromFilter } from 'lib/filterUtils'
import Tabs from 'components/filters/Tabs'
import { naturalFeedLayoutEnabled } from 'lib/mediaBuckets'
import { resetNaturalFeedSessionSnapshot } from 'lib/naturalFeedSession'
import { myStreamsEnabled } from 'lib/myStreamsFeature'

interface UserProfileProps {
    filter: Filter
    panel: PanelState
    quest?: Quest | null
    query?: string
    hideDuringScroll?: boolean
    setIsRefreshing?: (value: boolean) => void
    panelId?: string
}

interface UserQueryCacheData {
    followers_count?: number
    is_followed_by_current_user?: boolean
    [key: string]: unknown
}

export default function UserProfile(props: UserProfileProps) {
    const [hover, setHover] = useState(false)
    const [isFollowing, setIsFollowing] = useState(false)
    const [isLoadingFollow, setIsLoadingFollow] = useState(false)
    const [followersCount, setFollowersCount] = useState(0)
    const [followingCount, setFollowingCount] = useState(0)
    const [showFollowersModal, setShowFollowersModal] = useState(false)
    const [showFollowingModal, setShowFollowingModal] = useState(false)
    const [showProfileShareTray, setShowProfileShareTray] = useState(false)
    const [followers, setFollowers] = useState([])
    const [following, setFollowing] = useState([])
    const [loadingFollowers, setLoadingFollowers] = useState(false)
    const [loadingFollowing, setLoadingFollowing] = useState(false)
    const { panel, hide } = usePanelContext()
    const panelId = panel.panelId
    const filter = panel.filter
    const query = filter?.query
    const hasMyStreams = myStreamsEnabled()

    const DEBUG = useStore(state => state.DEBUG)
    const openEditProfileModal = useStore(state => state.actions.modalActions.openEditProfileModal)
    // Use a type assertion to avoid TypeScript error
    const animationState = useStore(state => state.searchHeaderAnimationStates?.[panelId]?.state)

    const [showFilterOptions, setShowFilterOptions] = useState<boolean>()

    const showOrder =
        !filter?.notifications && !filter?.history && !filter?.starred && !filter?.participants

    const isMeme = filter?.meme
    const searchBarPlaceholder = isMeme ? 'Search Meme' : 'Search Stream'
    const filterUserId = filter?.user_ordinals || filter?.user_music || filter?.user
    const { user: filterUser } = useGetUser(filterUserId)
    const profileUser = useMemo(
        () => resolveUser(filterUser, filterUserId),
        [filterUser, filterUserId],
    )
    const profileShareKey = profileUser?.id || filterUserId
    const profileShareName = profileUser?.username || profileUser?.name || 'this profile'
    const profileShareUrl = getShowcaseShareUrl('profile', profileShareKey)
    const profileShareCardUrl = getShowcaseShareCardUrl('profile', profileShareKey)
    const isOwnProfile = Boolean(
        gon.currentUser && filterUserId && String(filterUserId) === String(gon.currentUser.id),
    )

    // Profile banner. banner_url is versioned by blob id, so a changed banner is
    // a changed URL and the browser refetches on its own.
    const bannerUrl = profileUser?.banner_url
        ? `${window.knovApiUrl || ''}${profileUser.banner_url}`
        : null
    const [bannerFailed, setBannerFailed] = useState(false)
    useEffect(() => {
        setBannerFailed(false)
    }, [bannerUrl])

    const handleBannerUpdated = updatedUser => {
        // Merge only the banner fields: the update response is a full profile
        // payload whose counters/flags can differ in shape from the cached one.
        const patch = {
            id: updatedUser.id,
            has_banner: updatedUser.has_banner,
            banner_url: updatedUser.banner_url,
        }
        try {
            cacheUser(patch, true)
        } catch (e) {}
        try {
            queryClient.setQueryData(['user', filterUserId], (oldUser: object | null) =>
                oldUser ? { ...oldUser, ...patch } : oldUser,
            )
        } catch (e) {}
        if (String(updatedUser.id) === String(gon.currentUser?.id)) {
            try {
                window.gon.currentUser = { ...window.gon.currentUser, ...patch }
            } catch (e) {}
        }
    }

    const { openBannerPicker, bannerUploadUi } = useUserBannerUpload({
        user: profileUser,
        onUpdated: handleBannerUpdated,
    })
    const minStyles =
        filter?.notifications || filter?.history || filter?.starred || filter?.participants
            ? styles.min
            : null

    const memeControlContainerStyles = isMeme ? styles.memeControlContainer : null

    const slideToPanel = useSlideToPanel()
    const { openRight } = useOpenRight(panelId)

    const refreshStream = async () => {
        if (props.setIsRefreshing) {
            props.setIsRefreshing(true)
        }
        // The Music tab's songs live under the artist query, not the quest feed.
        if (filter?.user_music) {
            await queryClient.invalidateQueries({ queryKey: ['radioArtist', filter.user_music] })
            await queryClient.invalidateQueries({
                queryKey: ['userEarnings', String(filter.user_music)],
            })
            props.setIsRefreshing?.(false)
            return
        }
        // Invalidate and refetch quests when the panel mounts
        const newFilter = withDepth(filter)
        const queryKey = getQuestsQueryKey(
            newFilter,
            newFilter?.notifications ? { clear: true } : {},
        )

        // clear notification count if we're refreshing the notifications stream
        if (newFilter?.notifications) {
            await clearNotificationCount()
        }

        if (naturalFeedLayoutEnabled(newFilter)) {
            resetNaturalFeedSessionSnapshot(String(queryKey[1]))
        }
        await queryClient.invalidateQueries({ queryKey, exact: true })
        if (props.setIsRefreshing) {
            props.setIsRefreshing(false)
        }
    }

    const onClickStream = (ev: React.MouseEvent) => {
        ev.stopPropagation()
        ev.preventDefault()

        refreshStream()
    }

    const lastAction = useStore.getState().panels?.lastAction

    // We get the participantQuest from the prior panel via lastAction so we don't have to wait for the quest.
    let participantQuest = props.quest
    if (
        !participantQuest &&
        panel?.panelId === lastAction?.insert?.panelId &&
        lastAction?.questId
    ) {
        participantQuest = getCachedQuest(lastAction.questId)
    }
    const participants = useMemo(() => nonUserParticipants(participantQuest), [participantQuest])

    // Parse participant IDs from filter.participants key when no quest exists
    const participantIdsFromFilter = useMemo(
        () => getParticipantIdsFromFilter(filter),
        [filter?.participants],
    )
    const showStream = !filter?.participants

    const activeProfileTab = filter?.user_ordinals
        ? 'ordinals'
        : filter?.user_music
        ? 'music'
        : hasMyStreams && filter?.profile_thread_scope === 'replies'
        ? 'replies'
        : 'posts'
    const profileTabs = useMemo(
        () => [
            {
                key: 'posts',
                label: 'Posts',
                icon: FileText,
                testId: 'profile-tab-posts',
            },
            ...(hasMyStreams
                ? [
                      {
                          key: 'replies',
                          label: 'Replies',
                          icon: MessageCircle,
                          testId: 'profile-tab-replies',
                      },
                  ]
                : []),
            {
                key: 'ordinals',
                label: 'Ordinals',
                icon: Circle,
                testId: 'profile-tab-ordinals',
            },
            {
                key: 'music',
                label: 'Music',
                icon: Music,
                testId: 'profile-tab-music',
            },
        ],
        [hasMyStreams],
    )

    const handleProfileTabChange = (tab: 'posts' | 'replies' | 'ordinals' | 'music') => {
        const newFilter: Partial<Filter> = { ...filter }

        if (tab === 'ordinals' || tab === 'music') {
            delete newFilter.user
            delete newFilter.ordinals
            delete newFilter.profile_thread_scope
            delete newFilter.user_ordinals
            delete newFilter.user_music
            newFilter[tab === 'music' ? 'user_music' : 'user_ordinals'] = filterUserId
        } else {
            delete newFilter.user_ordinals
            delete newFilter.user_music
            newFilter.user = filterUserId
            delete newFilter.ordinals
            newFilter.profile_thread_scope = tab
        }

        useStore.getState().panels.editPanel(panelId, { filter: newFilter })
        const keyedFilter = withDepth(newFilter)
        queryClient.invalidateQueries({ queryKey: getQuestsQueryKey(keyedFilter), exact: true })

        const basePath = String(filterUser?.path || `/user/${filterUserId || ''}`).replace(
            /\/$/,
            '',
        )
        const nextPath =
            tab === 'ordinals'
                ? `${basePath}/ordinals`
                : tab === 'music'
                ? `${basePath}/music`
                : tab === 'replies'
                ? `${basePath}/replies`
                : basePath
        window.history.pushState({ filter: newFilter }, '', nextPath)

        // Keep behavior consistent with home stream tabs (persist filter)
        if (gon.currentUser) {
            api.updateUserSpaceOptions({ filter: newFilter })
        }
    }

    const animationClass =
        (animationState === 'fadeIn' && styles.selectedAnimationFadeIn) ||
        (animationState === 'fadeOut' && styles.selectedAnimationFadeOut) ||
        ''

    // Truncate user description to 160 characters
    const truncateDescription = (description: string) => {
        if (!description) return 'A Treechatter'
        if (description.length <= 160) return description
        return `${description.substring(0, 160)}...`
    }

    // Format member since date (Month YYYY)
    const formatMemberSince = (createdAt: string) => {
        if (!createdAt) return null
        const date = new Date(createdAt)
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' }
        return date.toLocaleDateString('en-US', options)
    }

    const userDescription = truncateDescription(profileUser?.bio || profileUser?.description || '')
    const showsFollowsYouLabel = Boolean(gon.currentUser && profileUser?.is_following_current_user)
    const hasKnownFollowState = useMemo(() => {
        if (!gon.currentUser) return true
        const followState = filterUser?.is_followed_by_current_user
        if (typeof followState === 'boolean') return true
        const fallbackFollowState = profileUser?.is_followed_by_current_user
        return typeof fallbackFollowState === 'boolean'
    }, [filterUser, profileUser, gon.currentUser])
    const isResolvingFollowState = Boolean(gon.currentUser) && !hasKnownFollowState

    const openSearch = (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (!filterUserId) return

        // Create a new search filter with user context
        // Mirrors Control > All search, but scoped to this profile user.
        const searchFilter = {
            search: true,
            query: '',
            user: filterUserId,
        }

        openRight(searchFilter)
    }

    const openProfileShareTray = (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (profileShareUrl) setShowProfileShareTray(true)
    }

    // Check if current user is following this user and set counts from cached user data
    useEffect(() => {
        const sourceUser = filterUser || profileUser
        if (!sourceUser) return

        if (gon.currentUser) {
            if (typeof sourceUser.is_followed_by_current_user === 'boolean') {
                setIsFollowing(sourceUser.is_followed_by_current_user)
            }
        } else {
            setIsFollowing(false)
        }

        // Keep counters in sync with freshest available user payload.
        setFollowersCount(sourceUser.followers_count || 0)
        setFollowingCount(sourceUser.following_count || 0)
    }, [filterUser, profileUser, gon.currentUser])

    const handleFollow = async (ev: React.MouseEvent) => {
        ev.preventDefault()
        ev.stopPropagation()

        if (isLoadingFollow || !hasKnownFollowState) return

        setIsLoadingFollow(true)
        try {
            const nextIsFollowing = !isFollowing
            // Self-follows are excluded from displayed counts, so toggling your own
            // follow (the own-posts-in-home preference) doesn't move them.
            const isOwnProfile = String(filterUserId) === String(gon.currentUser?.id)
            const followerDelta = isOwnProfile ? 0 : nextIsFollowing ? 1 : -1
            const nextFollowersCount = Math.max(0, followersCount + followerDelta)
            if (isFollowing) {
                await api.unfollowUser(filterUserId)
            } else {
                await api.followUser(filterUserId)
            }

            setIsFollowing(nextIsFollowing)
            setFollowersCount(nextFollowersCount)

            // Keep both caches in sync to avoid stale follow state clobbering local UI.
            const cachedUserPatch = {
                id: filterUserId,
                is_followed_by_current_user: nextIsFollowing,
                followers_count: nextFollowersCount,
            }
            cacheUser(cachedUserPatch, true)

            queryClient.setQueryData<UserQueryCacheData | null>(['user', filterUserId], oldUser => {
                if (!oldUser) return oldUser
                const oldFollowersCount =
                    typeof oldUser.followers_count === 'number' ? oldUser.followers_count : 0
                return {
                    ...oldUser,
                    is_followed_by_current_user: nextIsFollowing,
                    followers_count: Math.max(0, oldFollowersCount + followerDelta),
                }
            })
        } catch (error) {
            console.error('Error following/unfollowing user:', error)
        } finally {
            setIsLoadingFollow(false)
        }
    }

    const handleShowFollowers = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        setLoadingFollowers(true)
        try {
            const data = await api.getFollowers(filterUserId)
            if (data?.followers) {
                setFollowers(data.followers)
                setShowFollowersModal(true)
                useStore.setState({
                    isModalOpen: true,
                    modalParams: {
                        type: 'FollowList',
                        title: `${profileUser?.username || profileUser?.name}'s Followers`,
                        users: data.followers,
                        onFollowToggle: (userId, isFollowing) => {
                            // Update local state if needed
                            if (userId === gon.currentUser?.id) {
                                setIsFollowing(isFollowing)
                            }
                        },
                        customStyles: {
                            width: '600px',
                            maxWidth: '95vw',
                            maxHeight: '80vh',
                        },
                    },
                })
            }
        } catch (error) {
            console.error('Error fetching followers:', error)
        } finally {
            setLoadingFollowers(false)
        }
    }

    const handleShowFollowing = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        setLoadingFollowing(true)
        try {
            const data = await api.getFollowing(filterUserId)
            if (data?.following) {
                setFollowing(data.following)
                setShowFollowingModal(true)
                useStore.setState({
                    isModalOpen: true,
                    modalParams: {
                        type: 'FollowList',
                        title: `${profileUser?.username || profileUser?.name} is Following`,
                        users: data.following,
                        onFollowToggle: (userId, isFollowing) => {
                            // Update local state if needed
                            if (userId === gon.currentUser?.id) {
                                setIsFollowing(isFollowing)
                            }
                        },
                        customStyles: {
                            width: '600px',
                            maxWidth: '95vw',
                            maxHeight: '80vh',
                        },
                    },
                })
            }
        } catch (error) {
            console.error('Error fetching following:', error)
        } finally {
            setLoadingFollowing(false)
        }
    }

    const { hasBadge: hasOrdinalsBadge } = useUserOrdinalsBadge(filterUser?.id, {
        enabled: !!filterUser?.id,
    })

    return (
        <div
            className={cn(styles.userProfileComp, minStyles, animationClass)}
            style={{ visibility: hide || props.hideDuringScroll ? 'hidden' : 'visible' }}
            onClick={ev => slideToPanel()}
        >
            {DEBUG && (
                <div className={styles.debugContainer}>
                    <span className={styles.debugText}>
                        Filter:{' '}
                        <span
                            style={{ fontFamily: 'monospace', wordWrap: 'break-word' }}
                            onClick={() => {
                                const text = JSON.stringify(filter)
                                navigator.clipboard.writeText(text).catch(err => {
                                    console.error('Failed to copy filter to clipboard:', err)
                                })
                                // briefly bold the text
                                const spanElement = document.getElementById('filterText')
                                if (spanElement) {
                                    spanElement.style.fontWeight = 'bold'
                                    setTimeout(() => {
                                        spanElement.style.fontWeight = 'normal'
                                    }, 500)
                                }
                            }}
                            id="filterText"
                        >
                            {JSON.stringify(filter)}
                        </span>
                    </span>
                </div>
            )}

            {showStream && (
                <>
                    <div className={styles.bannerContainer}>
                        {bannerUrl && !bannerFailed ? (
                            <img
                                className={styles.bannerImage}
                                src={bannerUrl}
                                alt=""
                                onError={() => setBannerFailed(true)}
                            />
                        ) : (
                            <div className={styles.bannerDefault} />
                        )}
                        {isOwnProfile && (
                            <WithTooltip tip="Edit banner">
                                <button
                                    className={styles.bannerEditButton}
                                    onClick={ev => {
                                        ev.stopPropagation()
                                        openBannerPicker()
                                    }}
                                    aria-label="Edit banner"
                                    data-testid="user-profile-banner-edit-button"
                                >
                                    <Pencil size={14} />
                                </button>
                            </WithTooltip>
                        )}
                    </div>

                    <div className={cn(styles.userProfileContainer, memeControlContainerStyles)}>
                        <div className={styles.avatarRow}>
                            <div
                                className={cn(
                                    styles.avatarWrap,
                                    isMobile && styles.avatarWrapMobile,
                                )}
                            >
                                <div
                                    className={styles.userIconContainer}
                                    onClick={onClickStream}
                                    onMouseEnter={() => setHover(true)}
                                    onMouseLeave={() => setHover(false)}
                                >
                                    <UserStream
                                        user={profileUser}
                                        size="large"
                                        disable={true}
                                        showOrdinalsBadge={hasOrdinalsBadge}
                                        contextStyles={cn(
                                            styles.userIcon,
                                            isMobile && styles.mobileUserIcon,
                                        )}
                                    />
                                </div>
                            </div>

                            <div
                                className={styles.avatarRowActions}
                                onClick={ev => ev.stopPropagation()}
                            >
                                <WithTooltip tip="Share profile">
                                    <button
                                        className={styles.iconActionButton}
                                        onClick={openProfileShareTray}
                                        aria-label="Share profile"
                                        data-testid="user-profile-share-button"
                                    >
                                        <Share2 size={16} />
                                    </button>
                                </WithTooltip>
                                <WithTooltip tip="Search this profile">
                                    <button
                                        className={styles.iconActionButton}
                                        onClick={openSearch}
                                        aria-label="Search this profile"
                                        data-testid="user-profile-search-button"
                                    >
                                        <Search size={16} />
                                    </button>
                                </WithTooltip>
                                {gon.currentUser &&
                                    (isOwnProfile ? (
                                        <button
                                            className={cn(
                                                styles.followButton,
                                                styles.editProfileButton,
                                            )}
                                            onClick={ev => {
                                                ev.preventDefault()
                                                ev.stopPropagation()
                                                openEditProfileModal()
                                            }}
                                            data-testid="user-profile-edit-button"
                                        >
                                            <Pencil size={16} />
                                            <span>Edit profile</span>
                                        </button>
                                    ) : (
                                        <button
                                            className={cn(
                                                styles.followButton,
                                                isFollowing && styles.unfollowButton,
                                            )}
                                            onClick={handleFollow}
                                            disabled={isLoadingFollow || !hasKnownFollowState}
                                        >
                                            {isResolvingFollowState ? (
                                                <>
                                                    <span className={styles.loadingSpinner} />
                                                    <span>Loading...</span>
                                                </>
                                            ) : isFollowing ? (
                                                <>
                                                    <UserCheck size={16} />
                                                    <span>
                                                        {isLoadingFollow
                                                            ? 'Loading...'
                                                            : 'Unfollow'}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <UserPlus size={16} />
                                                    <span>
                                                        {isLoadingFollow ? 'Loading...' : 'Follow'}
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    ))}
                            </div>
                        </div>

                        <div className={styles.userName}>
                            {profileUser?.username || profileUser?.name || 'User'}
                        </div>

                        <div className={styles.userMetadataContainer}>
                            {showsFollowsYouLabel && (
                                <div className={styles.followsYouContainer}>
                                    <span className={styles.followsYouLabel}>Follows you</span>
                                </div>
                            )}

                            {profileUser?.created_at && (
                                <div className={styles.memberSinceContainer}>
                                    <span className={styles.memberSinceLabel}>
                                        Treechatter since{' '}
                                        {formatMemberSince(profileUser.created_at)}
                                    </span>
                                </div>
                            )}
                        </div>

                        {userDescription && (
                            <div className={styles.userDescriptionRow}>
                                <div className={styles.userDescription}>{userDescription}</div>
                            </div>
                        )}

                        <div className={styles.userActionRow}>
                            <div className={styles.userStats}>
                                <button
                                    className={styles.statButton}
                                    onClick={handleShowFollowers}
                                    disabled={loadingFollowers}
                                >
                                    <span className={styles.statCount}>{followersCount}</span>
                                    <span className={styles.statLabel}>Followers</span>
                                </button>
                                <button
                                    className={styles.statButton}
                                    onClick={handleShowFollowing}
                                    disabled={loadingFollowing}
                                >
                                    <span className={styles.statCount}>{followingCount}</span>
                                    <span className={styles.statLabel}>Following</span>
                                </button>
                            </div>
                        </div>

                        <ProfileEarnings
                            userId={filterUserId}
                            onOpenTab={tab => handleProfileTabChange(tab)}
                        />

                        <MessageUserButton
                            user={profileUser}
                            onOpenQuest={(questId, ev) =>
                                openRight({ questId }, { metricInputEvent: ev })
                            }
                            className={styles.messageWideButton}
                            testId="user-profile-message-button"
                        />
                    </div>

                    {isOwnProfile && bannerUploadUi}
                </>
            )}

            {showFilterOptions && (
                <div className={styles.filterOptionsContainer}>
                    <FilterOptions
                        origin="search-header"
                        filter={filter}
                        panelId={panelId}
                        panel={props.panel}
                        query={props.query}
                        close={() => setShowFilterOptions(!showFilterOptions)}
                        show={['default', 'teams']}
                        setShowFilterOptions={setShowFilterOptions}
                        showNoti
                        blur
                    />
                </div>
            )}

            {showStream && (
                <div className={styles.profileTabsRow} onClick={ev => ev.stopPropagation()}>
                    <Tabs
                        ariaLabel="Profile tabs"
                        activeTab={activeProfileTab}
                        onTabChange={handleProfileTabChange}
                        tabs={profileTabs}
                    />
                </div>
            )}

            {showOrder && (
                <div
                    className={cn(styles.streamOrderContainer)}
                    onClick={ev => ev.stopPropagation()}
                >
                    {filter?.user_music ? (
                        // The Music tab orders the artist's radio songs.
                        <ArtistSongsOrder panelId={panelId} />
                    ) : (
                        <StreamOrder
                            filter={filter}
                            panelId={panelId}
                            panel={props.panel}
                            query={query}
                        />
                    )}
                </div>
            )}

            {filter?.participants && (
                <div className={styles.participantsContainer}>
                    {participants && participants.length > 0 ? (
                        <Participants
                            participants={participants}
                            quest={participantQuest}
                            contextStyles={styles.participants}
                        />
                    ) : participantIdsFromFilter.length > 0 ? (
                        <ParticipantsFromIds
                            participantIds={participantIdsFromFilter}
                            contextStyles={styles.participants}
                        />
                    ) : null}
                </div>
            )}

            <ShareTray
                canShareImageCard={!!profileShareCardUrl}
                closeButtonTestId="profile-share-tray-close"
                dialogTestId="profile-share-tray"
                isOpen={showProfileShareTray}
                onDismiss={() => setShowProfileShareTray(false)}
                shareCardId={profileShareKey}
                shareCardUrl={profileShareCardUrl}
                shareKind="profile"
                shareText={`Check out ${profileShareName} on Treechat`}
                shareTitle={`${profileShareName} on Treechat`}
                shareUrl={profileShareUrl}
            />
        </div>
    )
}
